/**
 * 결제 상태 타입 정의
 */
export enum PaymentType {
    // 대기 중
    PENDING = "PENDING",
    VIRTUAL_ACCOUNT_ISSUED = "VIRTUAL_ACCOUNT_ISSUED",
    BANK_TRANSFER_PENDING = "BANK_TRANSFER_PENDING",
    SCHEDULE = "SCHEDULE",

    // 결제 완료
    PAID = "PAID",
    PARTIAL_PAID = "PARTIAL_PAID",

    // 실패 / 만료
    FAILED = "FAILED",
    PAYMENT_EXPIRED = "PAYMENT_EXPIRED",

    // 환불/취소 진행 중
    CANCELLED_PENDING = "CANCELLED_PENDING",
    PARTIAL_CANCELLED_PENDING = "PARTIAL_CANCELLED_PENDING",

    // 취소 완료
    CANCELLED = "CANCELLED",
    PARTIAL_CANCELLED = "PARTIAL_CANCELLED",

    // 환불 완료
    REFUNDED = "REFUNDED",
    PARTIAL_REFUNDED = "PARTIAL_REFUNDED",
}



export enum PaymentMethod {
    CARD = "CARD",                          //신용카드
    TRANSFER = "TRANSFER",                  //계좌이체
    VIRTUAL_ACCOUNT = "VIRTUAL_ACCOUNT",    //가상계좌
    MOBILE = "MOBILE",                      //휴대폰 소액결제
    PAYPAL = "PAYPAL",                      //해외결제
    KEYIN = "KEYIN",                        //수기결제
    BILLING_KEY = "BILLING_KEY",            //간편결제
}

/**
 * 결제 상태 그룹 타입
 */
export enum PaymentStatusGroup {
    PENDING = "PENDING",           // 대기 중
    COMPLETED = "COMPLETED",       // 완료
    FAILED = "FAILED",             // 실패/만료
    CANCELLING = "CANCELLING",     // 취소 진행 중
    CANCELLED = "CANCELLED",       // 취소 완료
    REFUNDED = "REFUNDED",         // 환불 완료
}

/**
 * 결제 타입을 결제 상태 그룹으로 매핑
 */
export const getPaymentStatusGroup = (paymentType: PaymentType): PaymentStatusGroup => {
    switch (paymentType) {
        case PaymentType.PENDING:
        case PaymentType.VIRTUAL_ACCOUNT_ISSUED:
        case PaymentType.BANK_TRANSFER_PENDING:
        case PaymentType.SCHEDULE:
            return PaymentStatusGroup.PENDING;

        case PaymentType.PAID:
        case PaymentType.PARTIAL_PAID:
            return PaymentStatusGroup.COMPLETED;

        case PaymentType.FAILED:
        case PaymentType.PAYMENT_EXPIRED:
            return PaymentStatusGroup.FAILED;

        case PaymentType.CANCELLED_PENDING:
        case PaymentType.PARTIAL_CANCELLED_PENDING:
            return PaymentStatusGroup.CANCELLING;

        case PaymentType.CANCELLED:
        case PaymentType.PARTIAL_CANCELLED:
            return PaymentStatusGroup.CANCELLED;

        case PaymentType.REFUNDED:
        case PaymentType.PARTIAL_REFUNDED:
            return PaymentStatusGroup.REFUNDED;

        default:
            return PaymentStatusGroup.PENDING;
    }
};

/**
 * 결제 타입별 대표 색상 반환
 * @param paymentType - 결제 타입
 * @returns 색상 HEX 코드
 */
export const getPaymentTypeColor = (paymentType: PaymentType): string => {
    const statusGroup = getPaymentStatusGroup(paymentType);

    switch (statusGroup) {
        case PaymentStatusGroup.PENDING:
            return 'var(--label-tertiary, #4f4f64)'; // (대기 중)

        case PaymentStatusGroup.COMPLETED:
            return 'var(--primary-filled-normal, #297aff)'; // (완료)

        case PaymentStatusGroup.FAILED:
            return 'var(--status-negative, #f83a3a)'; // (실패/만료)

        case PaymentStatusGroup.CANCELLING:
            return 'var(--label-tertiary, #4f4f64)'; // (취소 진행 중)

        case PaymentStatusGroup.CANCELLED:
            return 'var(--status-negative, #f83a3a)'; // (취소 완료)

        case PaymentStatusGroup.REFUNDED:
            return 'var(--status-negative, #f83a3a)'; // (환불 완료)

        default:
            return 'var(--label-tertiary, #4f4f64)'; // 기본 회색
    }
};

/**
 * 결제 상태 그룹별 색상 반환
 * @param statusGroup - 결제 상태 그룹
 * @returns 색상 HEX 코드
 */
export const getStatusGroupColor = (statusGroup: PaymentStatusGroup): string => {
    switch (statusGroup) {
        case PaymentStatusGroup.PENDING:
            return '#FFA500'; // 주황색
        case PaymentStatusGroup.COMPLETED:
            return '#28A745'; // 녹색
        case PaymentStatusGroup.FAILED:
            return '#DC3545'; // 빨간색
        case PaymentStatusGroup.CANCELLING:
            return '#FFC107'; // 노란색
        case PaymentStatusGroup.CANCELLED:
            return '#6C757D'; // 회색
        case PaymentStatusGroup.REFUNDED:
            return '#17A2B8'; // 청록색
        default:
            return '#6C757D'; // 기본 회색
    }
};

/**
 * 결제 타입 한글 번역
 */
export const getPaymentTypeLabel = (paymentType: PaymentType): string => {
    switch (paymentType) {
        case PaymentType.PENDING:
            return '결제 대기 중';
        case PaymentType.VIRTUAL_ACCOUNT_ISSUED:
            return '가상계좌 발급됨 (입금 대기)';
        case PaymentType.BANK_TRANSFER_PENDING:
            return '계좌이체 대기 중';
        case PaymentType.PAID:
            return '결제 완료';
        case PaymentType.PARTIAL_PAID:
            return '부분 결제 완료';
        case PaymentType.SCHEDULE:
            return '결제 예약 완료';
        case PaymentType.FAILED:
            return '결제 실패';
        case PaymentType.PAYMENT_EXPIRED:
            return '결제 기한 만료';
        case PaymentType.CANCELLED_PENDING:
            return '환불 진행 중';
        case PaymentType.PARTIAL_CANCELLED_PENDING:
            return '부분 환불 진행 중';
        case PaymentType.CANCELLED:
            return '결제 취소됨';
        case PaymentType.PARTIAL_CANCELLED:
            return '부분 결제 취소됨';
        case PaymentType.REFUNDED:
            return '결제 금액 환불됨';
        case PaymentType.PARTIAL_REFUNDED:
            return '부분 환불 완료됨';
        default:
            return '알 수 없음';
    }
};
